import { ReactNode, useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useNavigate, useParams } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ApiError, setAuthFailureHandler, setRoleProvider } from '@/api/client';
import { LoginPage } from '@/pages/LoginPage';
import { ChangePasswordPage } from '@/pages/ChangePasswordPage';
import { AdminLayout } from '@/pages/admin/AdminLayout';
import { MobileLayout } from '@/pages/mobile/MobileLayout';
import { useMobileShell } from '@/hooks/useMobileShell';
import { parseSiteId, pickDefaultSite, useSites } from '@/hooks/useCurrentSite';
import { addUploadedListener } from '@/features/upload-queue';
import { useCaptureRecovery } from '@/features/drafts';
import { ROUTES } from '@/lib/routes/registry';
import { landingPath, resolveVariant } from '@/lib/routes/logic';
import { PAGES } from '@/lib/routes/pages';
import type { RouteDef, Shell } from '@/lib/routes/types';
import { setCacheCleaner, useAuthStore } from '@/store/auth';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // 认证类错误重试没有意义，只会多刷几次 401
      retry: (failureCount, error) =>
        !(error instanceof ApiError && (error.isAuthFailure || error.status === 403)) &&
        failureCount < 2,
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      gcTime: 0,
    },
  },
});

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </QueryClientProvider>
  );
}

function AppRoutes() {
  const navigate = useNavigate();
  const restore = useAuthStore((s) => s.restore);
  const logout = useAuthStore((s) => s.logout);

  useEffect(() => {
    // 退出时清空查询缓存，否则下一个登录的人会先看到上一个人的数据闪一下
    setCacheCleaner(() => queryClient.clear());
    // 会话在服务端失效时（管理员强制下班、改过密码）由 api 层通知这里。
    // 走 logout 而不是直接跳转：本地的 token、缓存、队列监听都要一起清掉。
    setAuthFailureHandler(() => {
      void useAuthStore.getState().logout('expired');
      navigate('/login', { replace: true });
    });
    // 开发环境下 api 层据此报「这一页调了自己没权限的接口」（CR-040）
    setRoleProvider(() => useAuthStore.getState().user?.role ?? null);
  }, [navigate, logout]);

  // 冷启动恢复会话（CR-006）。刷新、App 被系统回收之后不再需要重新输密码，
  // 会话只由「下班打卡」「手动退出」「管理员强制下班」三件事结束。
  useEffect(() => {
    void restore();
  }, [restore]);

  /**
   * 队列传完一条，就让对应的列表重新去问服务端（CR-037）。
   *
   * 提交是**乐观**的：入队即成功，真正传上去要等 Worker。列表读的是服务端，
   * 所以在队列传完之前，工人刚提交的那条**不在列表里** —— 客户原话
   * 「点击上传后，列表是空的，需要等一会切换页面才回显示」。
   *
   * 注册在这里而不是各自的页面里：工人提交完经常立刻切走（回首页、去打卡），
   * 页面卸载了回调也就没了，而那正是东西传上去的时刻。
   */
  useEffect(
    () =>
      addUploadedListener((item) => {
        // 打卡：不刷新的话，界面要等 `me-shift` 的 60 秒轮询才知道服务端已经收到，
        // 而服务端可能把它判成了待复核甚至拒绝（超范围），界面上却显示一切正常
        if (item.kind === 'PUNCH') {
          void queryClient.invalidateQueries({ queryKey: ['me-shift'] });
          void queryClient.invalidateQueries({ queryKey: ['punches-today', item.siteId] });
          return;
        }

        // 概览的数字是服务端聚合出来的，四类记录都会改到它
        void queryClient.invalidateQueries({ queryKey: ['overview', item.siteId] });

        if (item.kind === 'DAILY_LOG') {
          void queryClient.invalidateQueries({ queryKey: ['my-logs'] });
          void queryClient.invalidateQueries({ queryKey: ['daily-logs'] });
        }

        if (item.kind === 'HIDDEN_WORK') {
          void queryClient.invalidateQueries({ queryKey: ['hidden-works'] });
          void queryClient.invalidateQueries({ queryKey: ['my-hidden-works'] });
          // 类别下拉是从历史记录里攒出来的，新写的类别要跟着出现
          void queryClient.invalidateQueries({ queryKey: ['hidden-categories'] });
        }

        if (item.kind === 'PURCHASE') {
          void queryClient.invalidateQueries({ queryKey: ['my-purchases'] });
          void queryClient.invalidateQueries({ queryKey: ['purchases'] });
          void queryClient.invalidateQueries({ queryKey: ['purchase-summary'] });
          void queryClient.invalidateQueries({ queryKey: ['purchase-categories'] });
          void queryClient.invalidateQueries({ queryKey: ['review-purchases', item.siteId] });
        }
      }),
    [],
  );

  // 拍照途中被安卓回收掉之后，把人送回那一页、把那张照片接回来（CR-037）
  useCaptureRecovery();

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />

      <Route
        path="/change-password"
        element={
          <RequireAuth allowPendingPassword>
            <ChangePasswordPage />
          </RequireAuth>
        }
      />

      {/*
        两套外壳的路由都从路由表生成（CR-040）。

        **用哪套外壳由设备决定，不看权限**（客户决定）：电脑浏览器是网页样式，
        手机、窄窗口、App 是 App 样式。AdminLayout 在窄屏上、MobileLayout 在宽屏上
        各自把人送到另一套外壳里对应的那一页。改之前是按「有没有管理类权限」分的 ——
        甲方（只看图纸 / 进度表 / 隐蔽工程）在电脑上打开，看到的是 App 样式。

        每一页进不进得去、渲染哪个组件，由 RegistryPage 查路由表决定。
      */}
      <Route
        element={
          <RequireAuth>
            <AdminLayout />
          </RequireAuth>
        }
      >
        {ROUTES.filter((r) => r.shell === 'desktop').map((route) => (
          <Route key={route.id} path={route.path} element={<RegistryPage route={route} shell="desktop" />} />
        ))}
      </Route>

      <Route
        element={
          <RequireAuth>
            <MobileLayout />
          </RequireAuth>
        }
      >
        {ROUTES.filter((r) => r.shell === 'mobile').map((route) => (
          <Route key={route.id} path={route.path} element={<RegistryPage route={route} shell="mobile" />} />
        ))}
      </Route>

      {/* 旧地址还有人收藏着，别让他们撞上空白页 */}
      <Route path="/dashboard" element={<HomeRedirect />} />
      <Route path="*" element={<HomeRedirect />} />
    </Routes>
  );
}

/**
 * 按路由表渲染一页（CR-040）。
 *
 * 第一个过门槛的变体生效；一个都过不了就回落脚点，**不解释** ——
 * 这不是他该看到的页面（CR-027：手输 URL 进一个接口全 403 的空壳，
 * 屏幕上的「上级还没上传图纸」是在骗他）。
 */
function RegistryPage({ route, shell }: { route: RouteDef; shell: Shell }) {
  const role = useAuthStore((s) => s.user?.role ?? null);
  const params = useParams<{ siteId: string }>();
  const variant = resolveVariant(route, role);

  if (!variant) {
    return <Navigate to={landingPath(role, shell, parseSiteId(params.siteId))} replace />;
  }

  const Page = PAGES[variant.page];
  // key 带上页面名：同一条路径按权限换了组件时整页重挂，别把上一个页面的状态带过去
  return <Page key={`${route.id}:${variant.page}`} />;
}

function RequireAuth({
  children,
  allowPendingPassword,
}: {
  children: ReactNode;
  allowPendingPassword?: boolean;
}) {
  const user = useAuthStore((s) => s.user);
  const restoring = useAuthStore((s) => s.restoring);

  // 正在用盘上的 refresh token 换回会话，先什么都不渲染 ——
  // 这里要是直接跳登录页，每次冷启动都会先闪一下登录框
  if (restoring) return null;

  if (!user) return <Navigate to="/login" replace />;

  // 首次登录必须先改密码，否则后端也会把所有业务接口拒掉
  if (user.mustChangePassword && !allowPendingPassword) {
    return <Navigate to="/change-password" replace />;
  }

  return <>{children}</>;
}

/**
 * 没有具体地址时去哪：按设备选外壳（CR-040），再落到那套外壳的落脚点。
 * 登录、改完密码、旧书签都走这里。
 */
function HomeRedirect() {
  const user = useAuthStore((s) => s.user);
  const restoring = useAuthStore((s) => s.restoring);
  const isMobileShell = useMobileShell();
  if (restoring) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.mustChangePassword) return <Navigate to="/change-password" replace />;
  if (isMobileShell) return <Navigate to="/m" replace />;
  return <SiteRedirect />;
}

/**
 * 网页端没指定工地时进哪里：上次待过的那个工地里第一个能看的页面；
 * 工地里一页都看不了，就去工地之外第一个能进的（工地列表永远能进）。
 *
 * 记住的工地可能已经归档、或者人已经被移出，所以要在服务端返回的可见列表里
 * 校验一遍再跳 —— 直接跳过去会撞上 403。
 */
function SiteRedirect() {
  const sites = useSites();
  const role = useAuthStore((s) => s.user?.role ?? null);
  if (sites.isLoading) return null;
  const target = pickDefaultSite(sites.data ?? []);
  return <Navigate to={landingPath(role, 'desktop', target?.id ?? null)} replace />;
}
